// A Service sheet vessel's season, and whether it's a current registration. The Service sheet holds several
// seasons of visits (a boat serviced each year, or several times a year, has a row per visit), so the Service
// page shows the current season's vessels by default, and the Washing Plan only ever asks to wash those. One
// rule for both:
//   • the "Season Noter" staff fill on the works is the vessel's service season: the noter on the vessel
//     itself, else the most common noter among its works;
//   • without one, an explicit Season column, then the visit's own dates (the season of the latest of them,
//     estimates included), then the boat's build season, each tagged with where it came from. A visit's dates
//     come before the build season: two visits of one boat share its build season, their dates tell them apart;
//   • a vessel is current unless its season comes from a real service signal (the noter, a Season column or a
//     date) and isn't the current season. A season that is only the boat's build year never makes a vessel
//     past: a boat built years ago is serviced this season like any other.
import { findServiceColumn, seasonIdFor } from '@technohull/shared';
import type { SheetGridColumn, SheetGridRow } from './hooks.js';
import { compareSeasonIds, detectDateColumn, detectSeasonColumn, normalizeSeasonId, type RowSeason } from './seasonTag.js';
import { serviceVesselDateFields } from './serviceDates.js';

const text = (v: unknown) => (typeof v === 'string' ? v : '');
/** the season a Season Noter / Season cell gives: the latest it names ("24-25, 25-26" is 25-26, a visit carried
 *  into the next season is that season's) */
const cellSeason = (v: unknown): string | null =>
  text(v).split(/[,;\n]+/).map((x) => normalizeSeasonId(x)).filter((x): x is string => !!x).sort(compareSeasonIds).at(-1) ?? null;

export interface ServiceSeasonColumns {
  /** the "Season Noter" column staff fill on the works */
  noterCol?: SheetGridColumn;
  /** an explicit Season / year column */
  seasonCol?: SheetGridColumn;
  /** the service / delivery date to fall back on (never a work's Expected date or a vessel's arrival dates) */
  dateCol?: SheetGridColumn;
  /** the visit's own dates: its five vessel dates (features/serviceDates.ts) and the service / delivery date */
  visitDateCols: SheetGridColumn[];
}

/** the Service sheet's columns a vessel's season is read from. */
export function serviceSeasonColumns(columns: SheetGridColumn[]): ServiceSeasonColumns {
  const expected = findServiceColumn('expected', columns);
  const dates = columns.filter((c) => c.type === 'DATE' || c.type === 'DATETIME');
  const fields = serviceVesselDateFields(dates);
  const arrival = new Set(fields.filter((f) => f.key === 'expectedArrival' || f.key === 'arrival').map((f) => f.col.id));
  const dateCol = detectDateColumn(columns.filter((c) => c.id !== expected?.id && !arrival.has(c.id)));
  const visitDateCols = fields.map((f) => f.col);
  if (dateCol && !visitDateCols.includes(dateCol)) visitDateCols.push(dateCol);
  return {
    noterCol: columns.find((c) => /season\s*note|σαιζον|σεζον\s*not/i.test(c.title)),
    seasonCol: detectSeasonColumn(columns),
    dateCol,
    visitDateCols,
  };
}

/** a visit's own dates (YYYY-MM-DD), earliest first. */
export function serviceVisitDates(v: SheetGridRow, cols: Pick<ServiceSeasonColumns, 'visitDateCols'>): string[] {
  const out = new Set<string>();
  for (const c of cols.visitDateCols) {
    const day = text(v.cells[c.id]).slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(day) && !Number.isNaN(Date.parse(day))) out.add(day);
  }
  return [...out].sort();
}

/** one vessel's season and where it came from (null when nothing says). `kids` gives a row's child rows. */
export function serviceVesselSeason(v: SheetGridRow, kids: (id: number) => readonly SheetGridRow[], cols: ServiceSeasonColumns): RowSeason | null {
  const { noterCol } = cols;
  if (noterCol) {
    const own = cellSeason(v.cells[noterCol.id]);
    if (own) return { id: own, source: 'column' };
    const counts = new Map<string, number>();
    const walk = (pid: number, depth: number) => {
      if (depth > 12) return;
      for (const k of kids(pid)) {
        const id = cellSeason(k.cells[noterCol.id]);
        if (id) counts.set(id, (counts.get(id) ?? 0) + 1);
        walk(k.id, depth + 1);
      }
    };
    walk(v.id, 0);
    let best: string | null = null;
    let n = 0;
    for (const [id, c] of counts) if (c > n) { best = id; n = c; }
    if (best) return { id: best, source: 'column' };
  }
  if (cols.seasonCol) {
    const fromCol = cellSeason(v.cells[cols.seasonCol.id]);
    if (fromCol) return { id: fromCol, source: 'column' };
  }
  const last = serviceVisitDates(v, cols).at(-1);
  if (last) return { id: seasonIdFor(new Date(last)), source: 'date' };
  return v.season ? { id: v.season, source: 'build' } : null;
}

/** whether a vessel with this season is a current registration (see the top of this file). */
export const isCurrentServiceSeason = (rs: RowSeason | null | undefined, currentSeason: string): boolean =>
  !rs || rs.source === 'build' || rs.id === currentSeason;
